import { HubConnectionBuilder } from '@microsoft/signalr';
import {
  Chess,
  Swal,
  copyText,
  createPlayableBoard,
  formatClock,
  refreshPlayableBoard,
  renderMoveList,
  selectPromotionIfNeeded
} from './chess-ui.js';
import { classifyIncomingPly, loadAuthoritativePosition, parseServerDateTime } from './online-game-state.js';
import { playMoveFeedback } from './move-feedback.js';
import { getDisconnectClaimState, DISCONNECT_GRACE_MILLISECONDS } from './disconnect-claim-policy.js';

const root = document.querySelector('[data-online-game]');
if (root) {
  const gameId = root.dataset.gameId;
  const hubUrl = root.dataset.hubUrl || '/hubs/game';
  const gameBaseUrl = root.dataset.gameBaseUrl || '/Online/Game/';
  const tournamentBaseUrl = root.dataset.tournamentBaseUrl || '/OnlineTournaments/Details/';
  const stockfishUrl = root.dataset.stockfishUrl || '/vendor/stockfish/stockfish-18-lite-single.js';
  const initialFen = new Chess().fen();
  const cover = root.querySelector('[data-connection-cover]');
  const connectionState = root.querySelector('[data-connection-state]');
  const moveList = root.querySelector('[data-online-moves]');
  const boardArea = root.querySelector('[data-online-board-area]');
  const whiteClock = root.querySelector('[data-online-clock="white"]');
  const blackClock = root.querySelector('[data-online-clock="black"]');
  const returnLiveButton = root.querySelector('[data-return-live-board]');
  const reviewIndicator = root.querySelector('[data-move-review-indicator]');
  const postGamePanel = root.querySelector('[data-post-game]');
  const liveActions = root.querySelector('[data-live-actions]');
  const mobileGameActions = root.querySelector('[data-mobile-game-actions]');
  const rematchButtons = [...root.querySelectorAll('[data-rematch]')];
  const newGameButtons = [...root.querySelectorAll('[data-new-game]')];
  const returnTournamentLinks = [...root.querySelectorAll('[data-return-tournament]')];
  const rematchStatuses = [...root.querySelectorAll('[data-rematch-status]')];
  const rematchDeclineButtons = [...root.querySelectorAll('[data-rematch-decline]')];
  const mobileResultSheet = root.querySelector('[data-mobile-result-sheet]');
  const mobileFinalResult = root.querySelector('[data-mobile-final-result]');
  const mobileFinalReason = root.querySelector('[data-mobile-final-reason]');
  const toggleAnalysisButton = root.querySelector('[data-toggle-analysis]');
  const inlineEngine = root.querySelector('[data-inline-engine]');
  const inlineLines = root.querySelector('[data-inline-lines]');
  const inlineScore = root.querySelector('[data-inline-score]');
  const inlineStatus = root.querySelector('[data-inline-engine-status]');
  const inlineDepth = root.querySelector('[data-inline-depth]');
  const analysisContext = root.querySelector('[data-analysis-context]');
  const resetAnalysisVariationButton = root.querySelector('[data-reset-analysis-variation]');
  const firstMovePanel = root.querySelector('[data-first-move-countdown]');
  const firstMoveSeconds = root.querySelector('[data-first-move-seconds]');
  const firstMoveLabel = root.querySelector('[data-first-move-label]');
  const disconnectPanel = root.querySelector('[data-disconnect-claim]');
  const disconnectCountdown = root.querySelector('[data-disconnect-countdown]');
  const disconnectMessage = root.querySelector('[data-disconnect-message]');
  const disconnectActions = root.querySelector('[data-disconnect-actions]');
  const spectatorNote = root.querySelector('[data-spectator-note]');
  const viewerBadge = root.querySelector('[data-viewer-badge]');
  const moveLiveButton = root.querySelector('[data-move-live]');
  const takebackPanel = root.querySelector('[data-takeback-request]');
  const takebackCountdown = root.querySelector('[data-takeback-countdown]');
  const takebackButtons = [...root.querySelectorAll('[data-request-takeback]')];
  const drawButtons = [...root.querySelectorAll('[data-offer-draw]')];
  const resignButtons = [...root.querySelectorAll('[data-resign]')];
  const cancelPremoveButtons = [...root.querySelectorAll('[data-cancel-premove]')];
  const newGameSheet = root.querySelector('[data-new-game-sheet]');
  const newGameTimeButtons = [...root.querySelectorAll('[data-new-time]')];
  const newGameModeButtons = [...root.querySelectorAll('[data-new-rated]')];
  const newGameSearchState = root.querySelector('[data-new-game-search-state]');
  const newGameStatus = root.querySelector('[data-new-game-status]');
  const cancelNewGameButton = root.querySelector('[data-cancel-new-game]');
  const chatEnabled = root.dataset.chatEnabled === 'true';
  const chatPanel = root.querySelector('[data-game-chat-panel]');
  const chatMessages = root.querySelector('[data-game-chat-messages]');
  const chatInput = root.querySelector('[data-game-chat-input]');
  const chatStatus = root.querySelector('[data-game-chat-status]');
  const chatBadges = [...root.querySelectorAll('[data-game-chat-badge]')];
  const openChatButtons = [...root.querySelectorAll('[data-open-game-chat]')];

  // Chessground retains this exact object in its callback. Never replace it.
  const game = new Chess();
  let ground = null;
  let snapshot = null;
  let moves = [];
  let currentPly = 0;
  let reviewPly = 0;
  let clockBase = {
    white: 0,
    black: 0,
    serverAt: Date.now(),
    whiteTurn: true,
    finished: false,
    started: false
  };
  let clockCorrection = {
    white: 0,
    black: 0,
    startedAt: 0,
    duration: 0
  };
  let clockInitialized = false;
  let clockHasRendered = false;
  let lastRenderedClocks = { white: 0, black: 0 };
  let serverOffsetMilliseconds = 0;
  let serverOffsetInitialized = false;
  let movePending = false;
  let syncPending = false;
  let drawOfferPromptOpen = false;
  let rematchPromptOpen = false;
  let rematchOfferPromptShown = false;
  let mobileResultDismissed = false;
  let finishedToastKey = null;
  let startingConnection = false;
  let redirecting = false;
  let engine = null;
  let engineReady = false;
  let engineVisible = false;
  let engineFen = '';
  let enginePendingFen = '';
  let enginePendingLabel = '';
  let engineAcceptingInfo = false;
  let engineWatchdog = 0;
  let engineRecoveryAttempts = 0;
  let analysisGame = null;
  let analysisRootPly = 0;
  let analysisMoves = [];
  let takebackCountdownValue = null;
  let takebackExpirySyncRequested = false;
  let pendingOptimisticMove = null;
  let recoveringPendingMove = false;
  let reconnecting = false;
  let queuedPremove = null;
  let premoveSelectionPending = false;
  let premoveSelectionToken = 0;
  let suppressNextPremoveUnset = false;
  let newGameRated = true;
  let selectedNewGameTime = newGameTimeButtons.find(button => button.classList.contains('selected'))
    ?? newGameTimeButtons[0]
    ?? null;
  let newGameSearch = null;
  let chatLoaded = false;
  let chatLoading = false;
  let chatUnread = 0;
  const chatMessageIds = new Set();
  const engineLines = new Map();

  const connection = new HubConnectionBuilder()
    .withUrl(hubUrl)
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds(context) {
        return Math.min(10_000, 750 * (2 ** Math.min(context.previousRetryCount, 4)));
      }
    })
    .build();

  connection.serverTimeoutInMilliseconds = 15_000;
  connection.keepAliveIntervalInMilliseconds = 5_000;

  connection.on('MovePlayed', payload => {
    if (!ground || !snapshot) {
      void syncFromServer(true);
      return;
    }
    applyServerMove(payload);
  });
  connection.on('GameFinished', payload => {
    applyRatingUpdate(payload);
    presentEnded(payload.result, payload.termination, payload.status || 'Finished');
    void syncFromServer(true);
  });
  connection.on('PresenceChanged', payload => {
    setServerTime(payload?.serverTimeUtc);
    applyPresence(payload);
    void syncFromServer(true);
  });
  connection.on('DrawOffered', () => {
    if (!snapshot?.isSpectator) void showDrawOffer();
  });
  connection.on('DrawDeclined', () => toast('پیشنهاد تساوی رد شد', 'info'));
  connection.on('TakebackRequested', payload => {
    if (!snapshot || snapshot.isSpectator) return;
    snapshot.opponentRequestedTakeback = true;
    snapshot.viewerRequestedTakeback = false;
    snapshot.takebackRequestedPly = payload?.ply ?? currentPly;
    snapshot.takebackExpiresAtUtc = payload?.takebackExpiresAtUtc ?? null;
    takebackExpirySyncRequested = false;
    renderTakebackState();
    toast('حریف درخواست بازگشت حرکت داده است', 'info');
  });
  connection.on('TakebackResolved', (payload, accepted) => {
    applyTakebackResult(payload, Boolean(accepted));
  });
  connection.on('RematchRequested', () => {
    if (!snapshot || snapshot.isSpectator) return;
    snapshot.opponentRequestedRematch = true;
    renderRematchState();
    void showRematchOffer();
  });
  connection.on('RematchDeclined', () => {
    if (!snapshot || snapshot.isSpectator) return;
    snapshot.viewerRequestedRematch = false;
    snapshot.opponentRequestedRematch = false;
    renderRematchState('حریف درخواست تکرار را رد کرد.');
    revealMobileResultSheet(true);
  });
  connection.on('RematchStarted', newGameId => navigateToGame(newGameId));
  connection.on('GameFound', gameCreated => {
    if (!newGameSearch || !gameCreated?.searchId) return;
    if (normalizeSearchId(gameCreated.searchId) !== normalizeSearchId(newGameSearch.id)) return;
    const matchedGameId = gameCreated.gameId;
    newGameSearch = null;
    renderNewGameSearchState('matched', 'حریف پیدا شد؛ در حال ورود به بازی…');
    navigateToGame(matchedGameId);
  });
  connection.on('GameChatMessage', message => receiveChatMessage(message));
  connection.onreconnecting(() => {
    reconnecting = true;
    if (newGameSearch) {
      newGameSearch.inFlight = false;
      renderNewGameSearchState('reconnecting', 'اتصال قطع شد؛ جست‌وجو پس از اتصال خودکار ادامه می‌یابد.');
    }
    setConnectionState(false, pendingOptimisticMove ? 'reconnecting… ذخیره حرکت' : 'reconnecting…');
  });
  connection.onreconnected(async () => {
    reconnecting = true;
    setConnectionState(false, pendingOptimisticMove ? 'reconnecting… ثبت حرکت' : 'در حال همگام‌سازی…');
    await joinGame({ recoverPending: true });
    if (chatPanel && !chatPanel.classList.contains('hidden')) await loadGameChat(true);
  });
  connection.onclose(() => {
    reconnecting = true;
    if (newGameSearch) {
      newGameSearch.inFlight = false;
      renderNewGameSearchState('reconnecting', 'در حال بازیابی اتصال و صف جست‌وجو…');
    }
    setConnectionState(false, pendingOptimisticMove ? 'reconnecting… حرکت محفوظ است' : 'reconnecting…');
    window.setTimeout(() => void startConnection(), 900);
  });

  drawButtons.forEach(button => button.addEventListener('click', offerDraw));
  resignButtons.forEach(button => button.addEventListener('click', resign));
  cancelPremoveButtons.forEach(button => button.addEventListener('click', cancelQueuedPremove));
  takebackButtons.forEach(button => button.addEventListener('click', requestTakeback));
  root.querySelector('[data-takeback-accept]').addEventListener('click', () => void respondTakeback(true));
  root.querySelector('[data-takeback-decline]').addEventListener('click', () => void respondTakeback(false));
  root.querySelector('[data-move-first]').addEventListener('click', () => showPly(0));
  root.querySelector('[data-move-prev]').addEventListener('click', () => showPly(reviewPly - 1));
  root.querySelector('[data-move-next]').addEventListener('click', () => showPly(reviewPly + 1));
  root.querySelector('[data-move-last]').addEventListener('click', goLive);
  moveLiveButton.addEventListener('click', goLive);
  returnLiveButton.addEventListener('click', goLive);
  rematchButtons.forEach(button => button.addEventListener('click', requestRematch));
  newGameButtons.forEach(button => button.addEventListener('click', openNewGameSheet));
  rematchDeclineButtons.forEach(button => button.addEventListener('click', declineRematch));
  root.querySelector('[data-close-mobile-result]')?.addEventListener('click', () => {
    mobileResultDismissed = true;
    if (mobileResultSheet) mobileResultSheet.hidden = true;
  });
  toggleAnalysisButton.addEventListener('click', toggleInlineAnalysis);
  inlineDepth.addEventListener('change', analyzeSelectedPosition);
  resetAnalysisVariationButton?.addEventListener('click', () => resetAnalysisPosition(reviewPly));
  root.querySelector('[data-claim-win]').addEventListener('click', () => void claimDisconnectedOpponent(true));
  root.querySelector('[data-claim-draw]').addEventListener('click', () => void claimDisconnectedOpponent(false));
  root.querySelector('[data-share-game]').addEventListener('click', () => void shareCurrentGame());
  openChatButtons.forEach(button => button.addEventListener('click', openGameChat));
  root.querySelector('[data-close-game-chat]')?.addEventListener('click', closeGameChat);
  root.querySelector('[data-game-chat-form]')?.addEventListener('submit', event => {
    event.preventDefault();
    void sendGameChatMessage();
  });
  root.querySelector('[data-close-new-game]')?.addEventListener('click', () => void closeNewGameSheet());
  newGameSheet?.addEventListener('click', event => {
    if (event.target === newGameSheet) void closeNewGameSheet();
  });
  newGameTimeButtons.forEach(button => button.addEventListener('click', () => {
    if (newGameSearch) return;
    selectedNewGameTime = button;
    newGameTimeButtons.forEach(item => item.classList.toggle('selected', item === button));
    void startNewGameSearch(button);
  }));
  newGameModeButtons.forEach(button => button.addEventListener('click', () => {
    if (newGameSearch) return;
    newGameRated = button.dataset.newRated === 'true';
    newGameModeButtons.forEach(item => item.classList.toggle('active', item === button));
  }));
  cancelNewGameButton?.addEventListener('click', () => void cancelNewGameSearch());

  document.addEventListener('keydown', event => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;
    if (event.key === 'ArrowLeft') showPly(reviewPly - 1);
    if (event.key === 'ArrowRight') showPly(reviewPly + 1);
    if (event.key === 'Home') showPly(0);
    if (event.key === 'End') goLive();
    if (event.key === 'Escape' && newGameSheet && !newGameSheet.hidden) {
      event.preventDefault();
      void closeNewGameSheet();
      return;
    }
    if (event.key === 'Escape' && (queuedPremove || premoveSelectionPending)) cancelQueuedPremove();
  });
  root.querySelector('#onlineBoard')?.addEventListener('contextmenu', event => {
    if (!queuedPremove && !premoveSelectionPending) return;
    event.preventDefault();
    cancelQueuedPremove();
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void syncFromServer(true);
  });
  window.addEventListener('focus', () => void syncFromServer(true));
  window.addEventListener('pagehide', () => {
    disposeEngine();
    if (connection.state !== 'Disconnected') void connection.stop();
  });

  void startConnection();
  window.setInterval(updateClocksAndGracePeriods, 40);
  window.setInterval(() => {
    if (!document.hidden) void syncFromServer(false);
  }, 1000);
  // Refresh the token lease while waiting. This is idempotent server-side and
  // also turns a silently stale connection into an observable invoke failure.
  window.setInterval(() => {
    if (newGameSearch) void resumeNewGameSearch();
  }, 10_000);

  async function startConnection() {
    if (startingConnection || connection.state !== 'Disconnected') return;
    startingConnection = true;
    reconnecting = true;
    setConnectionState(false, pendingOptimisticMove ? 'reconnecting… حرکت محفوظ است' : 'در حال اتصال…');
    try {
      await connection.start();
      await joinGame({ recoverPending: true });
    } catch {
      setConnectionState(false, pendingOptimisticMove ? 'reconnecting… حرکت محفوظ است' : 'اتصال ناموفق؛ تلاش مجدد…');
      if (cover && !pendingOptimisticMove) {
        cover.querySelector('strong').textContent = 'در حال تلاش برای اتصال مجدد به بازی…';
      }
      window.setTimeout(() => void startConnection(), 1400);
    } finally {
      startingConnection = false;
    }
  }

  async function joinGame({ recoverPending = false } = {}) {
    const requestStartedAt = Date.now();
    const joinedSnapshot = await connection.invoke('JoinGame', gameId);
    const responseReceivedAt = Date.now();
    setServerTime(joinedSnapshot.serverTimeUtc, requestStartedAt, responseReceivedAt);

    if (recoverPending && pendingOptimisticMove) {
      await reconcilePendingMove(joinedSnapshot);
      return;
    }

    applyJoinedSnapshot(joinedSnapshot);
    reconnecting = false;
    setConnectionState(true, 'متصل');
    await syncFromServer(true);
    if (newGameSearch) await resumeNewGameSearch();
  }

  async function openGameChat() {
    if (!chatEnabled || !chatPanel || snapshot?.isSpectator) return;
    chatPanel.classList.remove('hidden');
    chatUnread = 0;
    renderChatBadges();
    await loadGameChat(false);
    window.setTimeout(() => chatInput?.focus(), 40);
  }

  function closeGameChat() {
    chatPanel?.classList.add('hidden');
  }

  async function loadGameChat(force) {
    if (!chatEnabled || !chatPanel || chatLoading || (chatLoaded && !force)) return;
    if (connection.state !== 'Connected') {
      if (chatStatus) chatStatus.textContent = 'پس از اتصال مجدد، پیام‌ها دریافت می‌شوند.';
      return;
    }
    chatLoading = true;
    if (chatStatus) chatStatus.textContent = 'در حال دریافت پیام‌ها…';
    try {
      const history = await connection.invoke('GetGameChatMessages', gameId);
      for (const message of history ?? []) appendChatMessage(message);
      chatLoaded = true;
      if (chatStatus) chatStatus.textContent = '';
      scrollChatToEnd();
    } catch (error) {
      handleChatError(error);
    } finally {
      chatLoading = false;
    }
  }

  async function sendGameChatMessage() {
    const text = chatInput?.value.trim() ?? '';
    if (!text || text.length > 500 || connection.state !== 'Connected') {
      if (chatStatus) chatStatus.textContent = connection.state === 'Connected'
        ? 'پیام باید بین ۱ تا ۵۰۰ نویسه باشد.'
        : 'اتصال زنده برقرار نیست؛ چند لحظه دیگر دوباره تلاش کنید.';
      return;
    }
    const submit = root.querySelector('[data-game-chat-form] button[type="submit"]');
    if (submit) submit.disabled = true;
    if (chatStatus) chatStatus.textContent = '';
    try {
      const message = await connection.invoke(
        'SendGameChatMessage',
        gameId,
        createClientMessageId(),
        text
      );
      appendChatMessage(message);
      chatInput.value = '';
      scrollChatToEnd();
    } catch (error) {
      handleChatError(error);
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  function receiveChatMessage(message) {
    if (!chatEnabled || !message || String(message.gameId).toLowerCase() !== gameId.toLowerCase()) return;
    const inserted = appendChatMessage(message);
    if (!inserted) return;
    const panelOpen = chatPanel && !chatPanel.classList.contains('hidden');
    if (panelOpen) scrollChatToEnd();
    else {
      chatUnread += 1;
      renderChatBadges();
    }
  }

  function appendChatMessage(message) {
    if (!chatMessages || message?.id === undefined || message?.id === null) return false;
    const key = String(message.id);
    if (chatMessageIds.has(key)) return false;
    chatMessageIds.add(key);
    chatMessages.querySelector('[data-game-chat-empty]')?.remove();
    const item = document.createElement('article');
    item.className = 'game-chat-message';
    if (snapshot && message.senderColor === snapshot.viewerColor) item.classList.add('own');
    const body = document.createElement('div');
    body.textContent = message.message ?? '';
    const meta = document.createElement('small');
    const time = new Date(message.createdAtUtc);
    const localTime = Number.isNaN(time.getTime())
      ? ''
      : new Intl.DateTimeFormat('fa-IR', { hour: '2-digit', minute: '2-digit' }).format(time);
    meta.textContent = `${message.senderName ?? 'بازیکن'}${localTime ? ` • ${localTime}` : ''}`;
    item.append(body, meta);
    chatMessages.append(item);
    return true;
  }

  function scrollChatToEnd() {
    if (chatMessages) chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  function renderChatBadges() {
    chatBadges.forEach(badge => {
      badge.hidden = chatUnread === 0;
      badge.textContent = chatUnread > 99 ? '99+' : String(chatUnread);
    });
  }

  function handleChatError(error) {
    const raw = String(error?.message ?? error ?? '').toUpperCase();
    if (raw.includes('ONLINE_GAME_CHAT_DISABLED')) {
      openChatButtons.forEach(button => button.classList.add('hidden'));
      closeGameChat();
      return;
    }
    if (chatStatus) chatStatus.textContent = raw.includes('ONLINE_GAME_CHAT_RATE_LIMIT')
      ? 'پیام‌ها را کمی آهسته‌تر ارسال کنید.'
      : raw.includes('ONLINE_GAME_CHAT_PLAYERS_ONLY')
        ? 'گفت‌وگو فقط برای دو بازیکن همین بازی در دسترس است.'
        : 'پیام ارسال نشد؛ دوباره تلاش کنید.';
  }

  function createClientMessageId() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
      const value = Math.random() * 16 | 0;
      return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    });
  }

  function applyJoinedSnapshot(joinedSnapshot, { preserveOptimisticPosition = false } = {}) {
    snapshot = joinedSnapshot;
    moves = [...snapshot.moves].sort((a, b) => a.ply - b.ply);
    currentPly = moves.reduce((max, move) => Math.max(max, Number(move.ply)), 0);
    reviewPly = currentPly;
    if (!preserveOptimisticPosition) loadAuthoritativePosition(game, snapshot.fen);

    root.classList.toggle('spectator-mode', snapshot.isSpectator);
    root.querySelector('[data-white-name]').textContent = snapshot.whiteName;
    root.querySelector('[data-black-name]').textContent = snapshot.blackName;
    root.querySelector('[data-white-rating]').textContent = snapshot.whiteRating;
    root.querySelector('[data-black-rating]').textContent = snapshot.blackRating;
    root.querySelector('[data-white-guest]').classList.toggle('hidden', !snapshot.whiteIsGuest);
    root.querySelector('[data-black-guest]').classList.toggle('hidden', !snapshot.blackIsGuest);
    root.querySelector('[data-game-time-control]').textContent = formatTimeControl(snapshot.initialTimeSeconds, snapshot.incrementSeconds);
    root.querySelector('[data-rated-chip]').textContent = snapshot.isRated ? 'ریتد' : 'آزاد';
    root.querySelector('[data-rated-chip]').classList.toggle('unrated', !snapshot.isRated);
    viewerBadge.classList.toggle('hidden', !snapshot.isSpectator);
    spectatorNote.classList.toggle('hidden', !snapshot.isSpectator);
    boardArea.classList.toggle('orientation-black', snapshot.viewerColor === 'black');
    renderHeadToHead(snapshot.headToHead);
    applyPresence(snapshot);
    syncClocks(
      snapshot.whiteMilliseconds,
      snapshot.blackMilliseconds,
      snapshot.isWhiteTurn,
      snapshot.status !== 'Active',
      snapshot.clockStarted,
      snapshot.serverTimeUtc,
      clockInitialized ? 900 : 0
    );

    if (!ground) {
      ground = createPlayableBoard(root.querySelector('#onlineBoard'), game, handleBoardMove, {
        orientation: snapshot.viewerColor,
        enabled: canMoveNow(),
        refreshAfterMove: false,
        getGame: () => analysisGame ?? game,
        ...onlineBoardOptions()
      });
    } else {
      ground.set({ orientation: snapshot.viewerColor });
    }

    movePending = Boolean(preserveOptimisticPosition && pendingOptimisticMove);
    if (preserveOptimisticPosition) {
      refreshOnlineBoard(false, [pendingOptimisticMove.command.from, pendingOptimisticMove.command.to]);
    } else {
      renderReviewPosition();
    }
    renderTimeline();
    renderRematchState();
    renderTakebackState();
    renderEndOrLiveState(false);
    updateGracePanels();
    cover.classList.add('hidden');
  }

  async function handleBoardMove(move) {
    if (isAnalysisMode()) {
      analysisMoves.push({
        san: move.san,
        uci: `${move.from}${move.to}${move.promotion ?? ''}`,
        fen: analysisGame.fen()
      });
      refreshAnalysisBoard([move.from, move.to]);
      renderAnalysisContext();
      analyzeSelectedPosition();
      return;
    }
    await submitMove(move);
  }

  async function submitMove(move) {
    if (!snapshot || snapshot.isSpectator) {
      game.undo();
      renderReviewPosition();
      return;
    }

    if (reviewPly !== currentPly) {
      game.undo();
      goLive();
      toast('برای ادامه بازی ابتدا به وضعیت زنده برگردید', 'warning');
      return;
    }

    const moveBelongsToViewer = move.color === 'w'
      ? snapshot.viewerColor === 'white'
      : snapshot.viewerColor === 'black';

    if (movePending || !moveBelongsToViewer) {
      game.undo();
      refreshOnlineBoard(canMoveNow());
      return;
    }

    const command = {
      gameId,
      from: move.from,
      to: move.to,
      promotion: move.promotion ?? null,
      san: move.san
    };
    pendingOptimisticMove = {
      command,
      basePly: currentPly,
      uci: `${move.from}${move.to}${move.promotion ?? ''}`,
      fenAfter: game.fen()
    };
    movePending = true;
    refreshOnlineBoard(false, [move.from, move.to]);

    if (connection.state !== 'Connected') {
      reconnecting = true;
      setConnectionState(false, 'reconnecting… حرکت محفوظ است');
      if (connection.state === 'Disconnected') void startConnection();
      return;
    }

    await sendPendingMove();
  }

  async function sendPendingMove() {
    if (!pendingOptimisticMove || recoveringPendingMove || connection.state !== 'Connected') return;
    recoveringPendingMove = true;
    const pending = pendingOptimisticMove;
    try {
      const requestStartedAt = Date.now();
      const result = await connection.invoke('SubmitMove', pending.command);
      pendingOptimisticMove = null;
      reconnecting = false;
      applyServerMove(result, {
        requestStartedAt,
        responseReceivedAt: Date.now(),
        smoothDuration: 220
      });
      setConnectionState(true, 'متصل');
    } catch {
      reconnecting = true;
      setConnectionState(false, 'reconnecting… بررسی ثبت حرکت');
      recoveringPendingMove = false;
      if (connection.state === 'Connected') {
        await recoverPendingMove();
      } else if (connection.state === 'Disconnected') {
        void startConnection();
      }
      return;
    }
    recoveringPendingMove = false;
  }

  async function recoverPendingMove() {
    if (!pendingOptimisticMove || recoveringPendingMove || connection.state !== 'Connected') return;
    recoveringPendingMove = true;
    const pending = pendingOptimisticMove;
    try {
      const joined = await connection.invoke('JoinGame', gameId);
      await reconcilePendingMove(joined);
    } catch {
      setConnectionState(false, 'reconnecting… حرکت محفوظ است');
      if (connection.state === 'Disconnected') void startConnection();
    } finally {
      recoveringPendingMove = false;
    }
  }

  async function reconcilePendingMove(joinedSnapshot) {
    const pending = pendingOptimisticMove;
    if (!pending) {
      applyJoinedSnapshot(joinedSnapshot);
      reconnecting = false;
      setConnectionState(true, 'متصل');
      return;
    }

    const serverMoves = [...(joinedSnapshot.moves ?? [])].sort((a, b) => Number(a.ply) - Number(b.ply));
    const expectedMove = serverMoves.find(move => Number(move.ply) === pending.basePly + 1);
    const serverPly = serverMoves.reduce((max, move) => Math.max(max, Number(move.ply)), 0);
    if (expectedMove && normalizeUci(expectedMove.uci) === normalizeUci(pending.uci)) {
      pendingOptimisticMove = null;
      movePending = false;
      applyJoinedSnapshot(joinedSnapshot);
      reconnecting = false;
      setConnectionState(true, 'متصل');
      return;
    }

    if (serverPly !== pending.basePly || joinedSnapshot.status !== 'Active') {
      pendingOptimisticMove = null;
      movePending = false;
      applyJoinedSnapshot(joinedSnapshot);
      reconnecting = false;
      setConnectionState(true, 'همگام شد');
      return;
    }

    applyJoinedSnapshot(joinedSnapshot, { preserveOptimisticPosition: true });
    try {
      const requestStartedAt = Date.now();
      const result = await connection.invoke('SubmitMove', pending.command);
      pendingOptimisticMove = null;
      applyServerMove(result, {
        requestStartedAt,
        responseReceivedAt: Date.now(),
        smoothDuration: 180
      });
      reconnecting = false;
      setConnectionState(true, 'متصل');
    } catch {
      // The first request may have committed while its response was lost. Check once more
      // before treating the move as rejected and rolling the board back.
      const verified = await connection.invoke('JoinGame', gameId);
      const verifiedMove = [...(verified.moves ?? [])]
        .find(move => Number(move.ply) === pending.basePly + 1);
      if (verifiedMove && normalizeUci(verifiedMove.uci) === normalizeUci(pending.uci)) {
        pendingOptimisticMove = null;
        movePending = false;
        applyJoinedSnapshot(verified);
        reconnecting = false;
        setConnectionState(true, 'متصل');
        return;
      }

      if (connection.state !== 'Connected') {
        setConnectionState(false, 'reconnecting… حرکت محفوظ است');
        return;
      }

      pendingOptimisticMove = null;
      movePending = false;
      applyJoinedSnapshot(verified);
      reconnecting = false;
      setConnectionState(true, 'همگام شد');
    }
  }

  function applyServerMove(payload, timing = null) {
    if (!payload || !snapshot) return;
    setServerTime(payload.serverTimeUtc, timing?.requestStartedAt, timing?.responseReceivedAt);
    const payloadPly = Number(payload.ply ?? 0);
    if (pendingOptimisticMove
      && payloadPly === pendingOptimisticMove.basePly + 1
      && normalizeUci(`${payload.from}${payload.to}${payload.promotion ?? ''}`) === normalizeUci(pendingOptimisticMove.uci)) {
      pendingOptimisticMove = null;
    }
    const plyState = classifyIncomingPly(payloadPly, currentPly, movePending);
    if (plyState === 'stale') return;
    if (plyState === 'gap') {
      movePending = false;
      void syncFromServer(true);
      return;
    }

    snapshot.clockStarted = Boolean(payload.clockStarted);
    snapshot.firstMoveDeadlineUtc = payloadPly === 1
      ? payload.firstMoveDeadlineUtc
        ?? new Date((toMilliseconds(payload.serverTimeUtc) ?? serverNow()) + 20_000).toISOString()
      : null;
    snapshot.viewerRequestedTakeback = false;
    snapshot.opponentRequestedTakeback = false;
    snapshot.takebackRequestedPly = null;
    snapshot.takebackExpiresAtUtc = null;
    applyRatingUpdate(payload);
    snapshot.status = payload.isFinished ? 'Finished' : 'Active';
    syncClocks(
      payload.whiteMilliseconds,
      payload.blackMilliseconds,
      payload.isWhiteTurn,
      payload.isFinished,
      snapshot.clockStarted,
      payload.serverTimeUtc,
      timing?.smoothDuration ?? 260
    );

    if (plyState === 'duplicate') {
      movePending = false;
      if (reviewPly === currentPly) renderReviewPosition();
      if (payload.isFinished) presentEnded(payload.result, payload.termination, 'Finished');
      updateGracePanels();
      return;
    }

    const wasLive = reviewPly === currentPly;
    loadAuthoritativePosition(game, payload.fen);
    addOrReplaceMove({
      ply: payloadPly,
      uci: `${payload.from}${payload.to}${payload.promotion ?? ''}`,
      san: payload.san,
      fen: payload.fen
    });
    currentPly = payloadPly;
    playMoveFeedback();
    if (wasLive) reviewPly = currentPly;
    movePending = false;

    renderReviewPosition([payload.from, payload.to]);
    renderTimeline();
    updateGracePanels();
    renderActionAvailability();
    renderTakebackState();
    if (payload.isFinished) presentEnded(payload.result, payload.termination, 'Finished');
    else void executeQueuedPremoveIfReady();
  }

  async function syncFromServer(force) {
    if (!snapshot || !ground || syncPending || connection.state !== 'Connected') return;
    if (pendingOptimisticMove) return;
    if (!force && movePending) return;

    syncPending = true;
    try {
      const requestStartedAt = Date.now();
      const state = await connection.invoke('SyncGame', gameId, currentPly);
      applyServerSync(state, {
        requestStartedAt,
        responseReceivedAt: Date.now()
      });
      setConnectionState(true, 'متصل');
    } catch {
      if (force) setConnectionState(false, 'در حال همگام‌سازی مجدد…');
    } finally {
      syncPending = false;
    }
  }

  function applyServerSync(state, timing = null) {
    if (!state || !snapshot) return;
    setServerTime(state.serverTimeUtc, timing?.requestStartedAt, timing?.responseReceivedAt);
    const statePly = Number(state.ply ?? 0);
    const plyState = classifyIncomingPly(statePly, currentPly, movePending);
    if (plyState === 'stale') return;

    if (state.rematchGameId && !snapshot.isSpectator) {
      navigateToGame(state.rematchGameId);
      return;
    }

    const unseenMoves = (state.moves ?? [])
      .filter(move => Number(move.ply) > currentPly)
      .sort((a, b) => Number(a.ply) - Number(b.ply));
    const positionChanged = statePly !== currentPly || state.fen !== game.fen();
    const statusChanged = snapshot.status !== state.status;
    const transitionedFromActive = snapshot.status === 'Active' && state.status !== 'Active';
    const wasLive = reviewPly === currentPly;
    snapshot.status = state.status;
    snapshot.result = state.result;
    snapshot.termination = state.termination;
    const opponentRematchWasRequested = snapshot.opponentRequestedRematch;
    snapshot.viewerRequestedRematch = state.viewerRequestedRematch;
    snapshot.opponentRequestedRematch = state.opponentRequestedRematch;
    if (!snapshot.opponentRequestedRematch) rematchOfferPromptShown = false;
    snapshot.rematchGameId = state.rematchGameId;
    snapshot.clockStarted = state.clockStarted;
    snapshot.firstMoveDeadlineUtc = state.firstMoveDeadlineUtc;
    snapshot.whiteConnected = state.whiteConnected;
    snapshot.blackConnected = state.blackConnected;
    snapshot.opponentDisconnectedAtUtc = state.opponentDisconnectedAtUtc;
    snapshot.disconnectClaimAvailableAtUtc = state.disconnectClaimAvailableAtUtc;
    snapshot.viewerRequestedTakeback = state.viewerRequestedTakeback;
    snapshot.opponentRequestedTakeback = state.opponentRequestedTakeback;
    snapshot.takebackRequestedPly = state.takebackRequestedPly;
    snapshot.takebackExpiresAtUtc = state.takebackExpiresAtUtc;
    if (snapshot.viewerRequestedTakeback || snapshot.opponentRequestedTakeback) takebackExpirySyncRequested = false;

    if (positionChanged) {
      loadAuthoritativePosition(game, state.fen);
      unseenMoves.forEach(addOrReplaceMove);
      currentPly = statePly;
      if (wasLive) reviewPly = currentPly;
      movePending = false;
    }

    if (positionChanged || statusChanged) {
      renderReviewPosition();
      renderTimeline();
    }

    syncClocks(
      state.whiteMilliseconds,
      state.blackMilliseconds,
      state.isWhiteTurn,
      state.status !== 'Active',
      state.clockStarted,
      state.serverTimeUtc,
      positionChanged || statusChanged ? 260 : 850
    );
    applyPresence(state);
    renderRematchState();
    renderTakebackState();
    if (!opponentRematchWasRequested && snapshot.opponentRequestedRematch) void showRematchOffer();
    if (positionChanged && state.status === 'Active') void executeQueuedPremoveIfReady();
    updateGracePanels();
    renderActionAvailability();
    if (state.opponentOfferedDraw && !snapshot.isSpectator) void showDrawOffer();
    if (state.status !== 'Active') presentEnded(state.result, state.termination, state.status, false, transitionedFromActive);
  }

  function addOrReplaceMove(move) {
    const normalized = {
      ply: Number(move.ply),
      uci: move.uci,
      san: move.san,
      fen: move.fen
    };
    const index = moves.findIndex(item => Number(item.ply) === normalized.ply);
    if (index >= 0) moves[index] = normalized;
    else moves.push(normalized);
    moves.sort((a, b) => Number(a.ply) - Number(b.ply));
  }

  function showPly(ply) {
    reviewPly = Math.max(0, Math.min(Number(ply), currentPly));
    // Post-game review is always an isolated Chess instance. Opening the
    // Stockfish panel only adds evaluation; it is never the gate for legal
    // navigation or disposable variations.
    if (snapshot?.status !== 'Active') {
      resetAnalysisPosition(reviewPly);
    } else {
      renderReviewPosition();
    }
    renderTimeline();
    if (engineVisible && snapshot?.status === 'Active') analyzeSelectedPosition();
  }

  function goLive() {
    showPly(currentPly);
  }

  function renderReviewPosition(lastMoveOverride) {
    if (!ground || !snapshot) return;
    if (isAnalysisMode()) {
      refreshAnalysisBoard(lastMoveOverride);
      returnLiveButton.classList.add('hidden');
      return;
    }
    const atLive = reviewPly === currentPly;
    if (atLive) {
      const last = moves.at(-1);
      refreshOnlineBoard(canMoveNow(), lastMoveOverride ?? uciToSquares(last?.uci));
    } else {
      const fen = fenAtPly(reviewPly);
      const reviewed = new Chess(fen);
      const last = reviewPly > 0 ? moves.find(item => Number(item.ply) === reviewPly) : null;
      ground.set({
        fen,
        turnColor: reviewed.turn() === 'w' ? 'white' : 'black',
        check: reviewed.inCheck() ? (reviewed.turn() === 'w' ? 'white' : 'black') : false,
        lastMove: uciToSquares(last?.uci),
        movable: { color: undefined, dests: new Map() }
      });
    }

    // This prompt is meaningful only while following an actually live online game.
    returnLiveButton.classList.toggle('hidden', atLive || snapshot.status !== 'Active');
  }

  function renderTimeline() {
    renderMoveList(moveList, moves, reviewPly, showPly);
    const atLive = reviewPly === currentPly;
    const isLiveGame = snapshot?.status === 'Active';
    reviewIndicator.textContent = analysisMoves.length > 0
      ? `Variation از حرکت ${Math.ceil(analysisRootPly / 2).toLocaleString('fa-IR')} • ${analysisMoves.length.toLocaleString('fa-IR')} نیم‌حرکت آزمایشی`
      : atLive
      ? currentPly === 0 ? 'شروع بازی' : `${isLiveGame ? 'آخرین وضعیت' : 'آخرین حرکت'} • ${Math.ceil(currentPly / 2)} حرکت`
      : reviewPly === 0 ? 'شروع بازی' : `مرور حرکت ${Math.ceil(reviewPly / 2)} ${reviewPly % 2 ? 'سفید' : 'سیاه'}`;
    root.querySelector('[data-move-first]').disabled = reviewPly === 0;
    root.querySelector('[data-move-prev]').disabled = reviewPly === 0;
    root.querySelector('[data-move-next]').disabled = reviewPly >= currentPly;
    root.querySelector('[data-move-last]').disabled = reviewPly >= currentPly;
    moveLiveButton.classList.toggle('active', atLive);
    moveLiveButton.textContent = isLiveGame ? 'زنده' : 'آخرین';
  }

  function fenAtPly(ply) {
    if (ply <= 0) return initialFen;
    return moves.find(item => Number(item.ply) === ply)?.fen ?? game.fen();
  }

  function canMoveNow() {
    const readyToStart = snapshot?.clockStarted
      || (snapshot?.whiteConnected === true && snapshot?.blackConnected === true && snapshot?.firstMoveDeadlineUtc);
    return Boolean(snapshot
      && !snapshot.isSpectator
      && snapshot.status === 'Active'
      && readyToStart
      && reviewPly === currentPly
      && isMyTurn());
  }

  function canInteractWithLiveBoard() {
    const readyToStart = snapshot?.clockStarted
      || (snapshot?.whiteConnected === true && snapshot?.blackConnected === true && snapshot?.firstMoveDeadlineUtc);
    return Boolean(snapshot
      && !snapshot.isSpectator
      && snapshot.status === 'Active'
      && readyToStart
      && reviewPly === currentPly
      && !movePending
      && !pendingOptimisticMove);
  }

  function onlineBoardOptions() {
    const interactive = canInteractWithLiveBoard();
    return {
      movableColor: interactive ? snapshot?.viewerColor : undefined,
      premoveEnabled: interactive && !isMyTurn(),
      onPremoveSet: queuePremove,
      onPremoveUnset: clearQueuedPremoveFromBoard
    };
  }

  function refreshOnlineBoard(enabled, lastMove = undefined) {
    refreshPlayableBoard(ground, game, enabled, lastMove, onlineBoardOptions());
  }

  function isAnalysisMode() {
    return Boolean(analysisGame && snapshot?.status !== 'Active');
  }

  function resetAnalysisPosition(ply) {
    if (!snapshot || snapshot.status === 'Active') return;
    analysisRootPly = Math.max(0, Math.min(Number(ply), currentPly));
    reviewPly = analysisRootPly;
    analysisMoves = [];
    analysisGame = new Chess(fenAtPly(analysisRootPly));
    refreshAnalysisBoard();
    renderAnalysisContext();
    renderTimeline();
    analyzeSelectedPosition();
  }

  function refreshAnalysisBoard(lastMove = undefined) {
    if (!ground || !analysisGame) return;
    const mainlineLast = analysisRootPly > 0
      ? moves.find(item => Number(item.ply) === analysisRootPly)
      : null;
    const variationLast = analysisMoves.at(-1);
    refreshPlayableBoard(
      ground,
      analysisGame,
      true,
      lastMove ?? uciToSquares(variationLast?.uci ?? mainlineLast?.uci),
      {
        movableColor: analysisGame.turn() === 'w' ? 'white' : 'black',
        premoveEnabled: false
      }
    );
  }

  function renderAnalysisContext() {
    if (!analysisContext) return;
    const count = analysisMoves.length;
    analysisContext.textContent = count === 0
      ? 'موقعیت مستقل از بازی اصلی است؛ یک حرکت قانونی روی تخته امتحان کنید.'
      : `Variation آزمایشی: ${analysisMoves.map(move => move.san).join(' ')}`;
    if (resetAnalysisVariationButton) resetAnalysisVariationButton.hidden = count === 0;
  }

  async function queuePremove(from, to) {
    if (!canInteractWithLiveBoard() || isMyTurn()) return;
    const token = ++premoveSelectionToken;
    queuedPremove = { from, to, promotion: null };
    premoveSelectionPending = true;
    const promotion = await selectPromotionIfNeeded(game, from, to);
    if (token !== premoveSelectionToken || !queuedPremove) return;
    queuedPremove.promotion = promotion ?? null;
    premoveSelectionPending = false;
    renderPremoveControl();
    await executeQueuedPremoveIfReady();
  }

  function clearQueuedPremoveFromBoard() {
    if (suppressNextPremoveUnset) {
      suppressNextPremoveUnset = false;
      return;
    }
    premoveSelectionToken += 1;
    premoveSelectionPending = false;
    queuedPremove = null;
    renderPremoveControl();
  }

  function cancelQueuedPremove() {
    const hadQueuedPremove = Boolean(queuedPremove || premoveSelectionPending);
    const promotionDialogOpen = premoveSelectionPending;
    premoveSelectionToken += 1;
    premoveSelectionPending = false;
    queuedPremove = null;
    if (promotionDialogOpen) Swal.close();
    if (ground && hadQueuedPremove) {
      suppressNextPremoveUnset = true;
      ground.cancelPremove();
    }
    renderPremoveControl();
  }

  function renderPremoveControl() {
    const visible = Boolean(queuedPremove || premoveSelectionPending);
    cancelPremoveButtons.forEach(button => {
      button.hidden = !visible || button.dataset.playerColor !== snapshot?.viewerColor;
    });
  }

  async function executeQueuedPremoveIfReady() {
    if (!queuedPremove || premoveSelectionPending || !canMoveNow() || movePending) return;
    const queued = queuedPremove;
    queuedPremove = null;
    renderPremoveControl();
    premoveSelectionToken += 1;
    suppressNextPremoveUnset = true;
    ground.cancelPremove();
    let move = null;
    try {
      move = game.move({ from: queued.from, to: queued.to, promotion: queued.promotion ?? 'q' });
    } catch {
      move = null;
    }
    if (!move) {
      toast('پیش‌حرکت بعد از حرکت حریف قانونی نبود و لغو شد', 'warning');
      renderReviewPosition();
      return;
    }
    await submitMove(move, ground, { premove: true });
  }

  async function requestTakeback() {
    if (!snapshot || snapshot.isSpectator || snapshot.status !== 'Active' || currentPly === 0) return;
    takebackButtons.forEach(button => { button.disabled = true; });
    try {
      const result = await connection.invoke('RequestTakeback', gameId);
      snapshot.viewerRequestedTakeback = true;
      snapshot.opponentRequestedTakeback = false;
      snapshot.takebackRequestedPly = result.ply;
      snapshot.takebackExpiresAtUtc = result.takebackExpiresAtUtc ?? null;
      takebackExpirySyncRequested = false;
      renderTakebackState();
      toast('درخواست بازگشت برای حریف ارسال شد', 'success');
    } catch (error) {
      toast(readableError(error), 'error');
    } finally {
      renderActionAvailability();
    }
  }

  async function respondTakeback(accept) {
    if (!snapshot?.opponentRequestedTakeback) return;
    takebackPanel.classList.add('busy');
    try {
      const result = await connection.invoke('RespondToTakeback', gameId, accept);
      applyTakebackResult(result, accept);
    } catch (error) {
      toast(readableError(error), 'error');
      await syncFromServer(true);
    } finally {
      takebackPanel.classList.remove('busy');
    }
  }

  function applyTakebackResult(result, accepted) {
    if (!snapshot || !result) return;
    snapshot.viewerRequestedTakeback = false;
    snapshot.opponentRequestedTakeback = false;
    snapshot.takebackRequestedPly = null;
    snapshot.takebackExpiresAtUtc = null;
    takebackExpirySyncRequested = false;
    if (result.reverted) {
      cancelQueuedPremove();
      moves = [...(result.moves ?? [])].sort((a, b) => Number(a.ply) - Number(b.ply));
      currentPly = Number(result.ply ?? moves.length);
      reviewPly = currentPly;
      loadAuthoritativePosition(game, result.fen);
      snapshot.clockStarted = result.clockStarted;
      snapshot.firstMoveDeadlineUtc = result.firstMoveDeadlineUtc;
      snapshot.status = 'Active';
      syncClocks(result.whiteMilliseconds, result.blackMilliseconds, result.isWhiteTurn, false,
        result.clockStarted, result.serverTimeUtc, 180);
      renderReviewPosition();
      renderTimeline();
      toast('آخرین حرکت با تنظیمات تعیین‌شده توسط مدیر بازگردانده شد', 'success');
    } else if (!accepted) {
      toast('درخواست بازگشت رد شد', 'info');
    }
    renderTakebackState();
    renderActionAvailability();
  }

  function renderTakebackState() {
    if (!snapshot) return;
    const showIncoming = snapshot.status === 'Active' && !snapshot.isSpectator && snapshot.opponentRequestedTakeback;
    takebackPanel.classList.toggle('hidden', !showIncoming);
    takebackButtons.forEach(button => {
      button.classList.toggle('pending', Boolean(snapshot.viewerRequestedTakeback));
      button.querySelector('span').textContent = snapshot.viewerRequestedTakeback ? '…' : '↶';
      button.title = snapshot.viewerRequestedTakeback
        ? takebackTitle('منتظر پاسخ حریف')
        : 'درخواست بازگشت آخرین حرکت';
    });
    renderTakebackCountdown();
  }

  function renderTakebackCountdown() {
    if (!snapshot || (!snapshot.viewerRequestedTakeback && !snapshot.opponentRequestedTakeback)) {
      takebackCountdownValue = null;
      if (takebackCountdown) takebackCountdown.textContent = '';
      return;
    }
    const expiry = toMilliseconds(snapshot.takebackExpiresAtUtc);
    if (expiry === null) {
      if (takebackCountdown && takebackCountdownValue !== 'none') {
        takebackCountdownValue = 'none';
        takebackCountdown.textContent = 'بدون محدودیت زمانی';
      }
      return;
    }
    const seconds = Math.max(0, Math.ceil((expiry - serverNow()) / 1000));
    if (takebackCountdown && takebackCountdownValue !== seconds) {
      takebackCountdownValue = seconds;
      takebackCountdown.textContent = `${seconds} ثانیه برای پاسخ`;
    }
    if (seconds > 0 || takebackExpirySyncRequested) return;
    takebackExpirySyncRequested = true;
    snapshot.viewerRequestedTakeback = false;
    snapshot.opponentRequestedTakeback = false;
    snapshot.takebackRequestedPly = null;
    snapshot.takebackExpiresAtUtc = null;
    renderTakebackState();
    renderActionAvailability();
    toast('مهلت درخواست بازگشت به پایان رسید', 'info');
    void syncFromServer(true);
  }

  function takebackTitle(prefix) {
    const expiry = toMilliseconds(snapshot?.takebackExpiresAtUtc);
    if (expiry === null) return `${prefix} • بدون انقضا`;
    return `${prefix} • ${Math.max(0, Math.ceil((expiry - serverNow()) / 1000))} ثانیه`;
  }

  async function offerDraw() {
    if (!snapshot?.clockStarted) return;
    const answer = await Swal.fire({
      title: 'پیشنهاد تساوی؟',
      text: 'این پیشنهاد برای حریف ارسال می‌شود.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'ارسال پیشنهاد',
      cancelButtonText: 'انصراف'
    });
    if (!answer.isConfirmed) return;
    try {
      await connection.invoke('OfferDraw', gameId);
      toast('پیشنهاد تساوی ارسال شد', 'success');
    } catch (error) {
      toast(readableError(error), 'error');
    }
  }

  async function showDrawOffer() {
    if (drawOfferPromptOpen || clockBase.finished || snapshot?.isSpectator) return;
    drawOfferPromptOpen = true;
    try {
      const answer = await Swal.fire({
        title: 'پیشنهاد تساوی',
        text: 'حریف پیشنهاد تساوی داده است.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'قبول',
        cancelButtonText: 'رد'
      });
      await connection.invoke('RespondToDraw', gameId, answer.isConfirmed);
    } catch (error) {
      toast(readableError(error), 'error');
    } finally {
      drawOfferPromptOpen = false;
      void syncFromServer(true);
    }
  }

  async function resign() {
    if (!snapshot?.clockStarted) return;
    const answer = await Swal.fire({
      title: 'تسلیم می‌شوی؟',
      text: 'این کار قابل بازگشت نیست.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'بله، تسلیم',
      cancelButtonText: 'ادامه بازی',
      confirmButtonColor: '#d94d52'
    });
    if (!answer.isConfirmed) return;
    try {
      await connection.invoke('Resign', gameId);
      await syncFromServer(true);
    } catch (error) {
      toast(readableError(error), 'error');
    }
  }

  async function claimDisconnectedOpponent(claimVictory) {
    if (!currentDisconnectClaim().ready) { updateGracePanels(); return; }
    const label = claimVictory ? 'پیروزی' : 'تساوی';
    const answer = await Swal.fire({
      title: `اعلام ${label}؟`,
      text: claimVictory
        ? 'مهلت بازگشت حریف تمام شده و بازی به سود شما ثبت می‌شود.'
        : 'مهلت بازگشت حریف تمام شده و بازی مساوی ثبت می‌شود.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `ثبت ${label}`,
      cancelButtonText: 'ادامه انتظار'
    });
    if (!answer.isConfirmed || !currentDisconnectClaim().ready) { updateGracePanels(); return; }
    try {
      await connection.invoke('ClaimDisconnectedOpponent', gameId, claimVictory);
      await syncFromServer(true);
    } catch (error) {
      toast(readableError(error), 'error');
      await syncFromServer(true);
    }
  }

  async function requestRematch() {
    if (connection.state !== 'Connected' || snapshot?.isSpectator || isTournamentGame()) return;
    let navigationStarted = false;
    setRematchPresentation('در حال ارسال…', 'در حال ارسال درخواست بازی مجدد…', true);
    setRematchDeclineVisibility(false);
    try {
      const result = await connection.invoke('RequestRematch', gameId);
      if (result.created && result.gameId) {
        setRematchPresentation('در حال شروع…', 'درخواست پذیرفته شد؛ بازی جدید در حال باز شدن است.', true);
        navigationStarted = true;
        navigateToGame(result.gameId);
        return;
      }
      snapshot.viewerRequestedRematch = true;
      renderRematchState();
    } catch (error) {
      toast(readableError(error), 'error');
    } finally {
      if (!navigationStarted) renderRematchState();
    }
  }

  async function declineRematch() {
    if (connection.state !== 'Connected'
      || snapshot?.isSpectator
      || !snapshot?.opponentRequestedRematch
      || snapshot.viewerRequestedRematch) return;
    setRematchPresentation('در حال رد…', 'در حال ثبت پاسخ درخواست بازی مجدد…', true);
    setRematchDeclineVisibility(true, true);
    try {
      await connection.invoke('DeclineRematch', gameId);
      snapshot.opponentRequestedRematch = false;
      snapshot.viewerRequestedRematch = false;
      rematchOfferPromptShown = false;
      renderRematchState('درخواست بازی مجدد رد شد.');
    } catch (error) {
      toast(readableError(error), 'error');
      renderRematchState();
    }
  }

  async function showRematchOffer() {
    if (!snapshot
      || snapshot.isSpectator
      || snapshot.status === 'Active'
      || !snapshot.opponentRequestedRematch
      || snapshot.viewerRequestedRematch
      || rematchPromptOpen
      || rematchOfferPromptShown) return;

    if (matchMedia('(max-width: 900px)').matches) {
      revealMobileResultSheet(true);
      rematchOfferPromptShown = true;
      return;
    }

    rematchPromptOpen = true;
    rematchOfferPromptShown = true;
    try {
      const answer = await Swal.fire({
        title: 'درخواست تکرار بازی',
        html: `<div class="rematch-swal-copy"><strong>${escapeHtml(snapshot.viewerColor === 'white' ? snapshot.blackName : snapshot.whiteName)}</strong><span>درخواست بازی مجدد با همین کنترل زمانی را داده است.</span></div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'قبول و شروع بازی',
        cancelButtonText: 'بعداً',
        reverseButtons: true,
        focusConfirm: true,
        customClass: { popup: 'rematch-swal-popup' }
      });
      if (answer.isConfirmed) await requestRematch();
    } finally {
      rematchPromptOpen = false;
    }
  }

  function renderRematchState(message = '') {
    if (!snapshot || snapshot.status === 'Active' || snapshot.isSpectator || isTournamentGame()) return;
    if (snapshot.rematchGameId) {
      navigateToGame(snapshot.rematchGameId);
      return;
    }
    if (snapshot.opponentRequestedRematch && !snapshot.viewerRequestedRematch) {
      setRematchPresentation('قبول بازی مجدد', message || 'حریف درخواست بازی مجدد با همین زمان را داده است.', false);
      setRematchDeclineVisibility(true);
      revealMobileResultSheet();
      void showRematchOffer();
      return;
    }
    if (snapshot.viewerRequestedRematch) {
      setRematchPresentation('درخواست ارسال شد', message || 'منتظر پذیرش حریف هستیم…', true);
      setRematchDeclineVisibility(false);
      return;
    }
    setRematchPresentation('بازی مجدد', message, false);
    setRematchDeclineVisibility(false);
  }

  function setRematchPresentation(label, message, disabled) {
    rematchButtons.forEach(button => {
      button.textContent = label;
      button.disabled = disabled;
    });
    rematchStatuses.forEach(status => { status.textContent = message; });
  }

  function setRematchDeclineVisibility(visible, disabled = false) {
    rematchDeclineButtons.forEach(button => {
      button.classList.toggle('hidden', !visible);
      button.disabled = disabled;
    });
  }

  function revealMobileResultSheet(force = false) {
    if (force) mobileResultDismissed = false;
    if (mobileResultSheet && !mobileResultDismissed) mobileResultSheet.hidden = false;
  }

  function isTournamentGame() {
    return Boolean(snapshot?.tournamentSlug);
  }

  function configureTournamentPostGameActions() {
    const tournamentGame = isTournamentGame();
    rematchButtons.forEach(button => button.classList.toggle('hidden', tournamentGame || snapshot?.isSpectator));
    newGameButtons.forEach(button => button.classList.toggle('hidden', tournamentGame || snapshot?.isSpectator));
    rematchStatuses.forEach(status => status.classList.toggle('hidden', tournamentGame || snapshot?.isSpectator));
    rematchDeclineButtons.forEach(button => button.classList.add('hidden'));
    returnTournamentLinks.forEach(link => {
      link.classList.toggle('hidden', !tournamentGame);
      if (tournamentGame) link.href = `${tournamentBaseUrl}${encodeURIComponent(snapshot.tournamentSlug)}`;
    });
  }

  function openNewGameSheet() {
    if (!newGameSheet || snapshot?.isSpectator || isTournamentGame()) return;
    newGameSheet.hidden = false;
    document.body.classList.add('dialog-open');
    renderNewGameSearchState(newGameSearch ? 'queued' : 'idle',
      newGameSearch ? 'جست‌وجوی فعال با قطع و وصل اینترنت حفظ می‌شود.' : 'زمان و نوع بازی را انتخاب کنید.');
  }

  async function closeNewGameSheet() {
    if (newGameSearch) await cancelNewGameSearch();
    if (newGameSheet) newGameSheet.hidden = true;
    document.body.classList.remove('dialog-open');
  }

  async function startNewGameSearch(timeButton = selectedNewGameTime) {
    if (newGameSearch || !timeButton) return;
    const initialSeconds = Number(timeButton.dataset.newTime);
    const incrementSeconds = Number(timeButton.dataset.newIncrement);
    if (!Number.isFinite(initialSeconds) || !Number.isFinite(incrementSeconds)) return;

    newGameSearch = {
      id: createSearchId(),
      initialSeconds,
      incrementSeconds,
      rated: newGameRated,
      inFlight: false,
      cancelled: false,
      cancelling: false,
      recoveryAttempts: 0
    };
    renderNewGameSearchState('connecting', `فرمت ${formatTimeControl(initialSeconds, incrementSeconds)} · آماده‌سازی اتصال…`);
    await resumeNewGameSearch();
  }

  async function resumeNewGameSearch() {
    const search = newGameSearch;
    if (!search || search.cancelled || search.inFlight || redirecting) return;
    if (search.cancelling) {
      await resumeNewGameCancellation(search);
      return;
    }
    if (connection.state !== 'Connected') {
      renderNewGameSearchState('reconnecting', 'در حال اتصال مجدد؛ توکن همین جست‌وجو حفظ شده است.');
      if (connection.state === 'Disconnected') void startConnection();
      return;
    }

    search.inFlight = true;
    renderNewGameSearchState('queued', `فرمت ${formatTimeControl(search.initialSeconds, search.incrementSeconds)} · منتظر حریف سازگار…`);
    try {
      const state = await invokeMatchmaking(
        'FindMatchV2',
        search.id,
        search.initialSeconds,
        search.incrementSeconds,
        search.rated);
      if (newGameSearch !== search || search.cancelled) return;
      search.inFlight = false;
      search.recoveryAttempts = 0;
      if (String(state?.status).toLowerCase() === 'matched' && state?.gameId) {
        newGameSearch = null;
        renderNewGameSearchState('matched', 'حریف پیدا شد؛ در حال ورود به بازی…');
        navigateToGame(state.gameId);
        return;
      }
      renderNewGameSearchState('queued', `فرمت ${formatTimeControl(search.initialSeconds, search.incrementSeconds)} · در صف جست‌وجو`);
    } catch (error) {
      if (newGameSearch !== search || search.cancelled) return;
      search.inFlight = false;
      if (error?.code === 'matchmaking_ack_timeout' && search.recoveryAttempts < 1) {
        search.recoveryAttempts += 1;
        renderNewGameSearchState('reconnecting', 'پاسخ سرور نرسید؛ اتصال تازه و همین جست‌وجو بازیابی می‌شود.');
        recycleMatchmakingConnection();
        return;
      }
      if (connection.state !== 'Connected') {
        renderNewGameSearchState('reconnecting', 'اتصال قطع شد؛ پس از اتصال همین جست‌وجو از سر گرفته می‌شود.');
        return;
      }
      if (search.recoveryAttempts < 1 && !isPermanentMatchmakingError(error)) {
        search.recoveryAttempts += 1;
        renderNewGameSearchState('reconnecting', 'ارتباط بازی در حال بازیابی است؛ همین جست‌وجو دوباره ثبت می‌شود.');
        recycleMatchmakingConnection();
        return;
      }
      newGameSearch = null;
      renderNewGameSearchState('error', matchmakingError(error));
    }
  }

  async function cancelNewGameSearch() {
    const search = newGameSearch;
    if (!search) {
      renderNewGameSearchState('idle', 'زمان و نوع بازی را انتخاب کنید.');
      return;
    }
    if (search.cancelling) return;
    search.cancelling = true;
    search.inFlight = false;
    renderNewGameSearchState('cancelling', 'در حال لغو امن جست‌وجو…');
    await resumeNewGameCancellation(search);
  }

  async function resumeNewGameCancellation(search) {
    if (newGameSearch !== search || search.cancelled || search.inFlight) return;
    if (connection.state !== 'Connected') {
      renderNewGameSearchState('reconnecting', 'پس از بازیابی اتصال، لغو نهایی می‌شود.');
      if (connection.state === 'Disconnected') void startConnection();
      return;
    }
    search.inFlight = true;
    try {
      const state = await invokeMatchmaking('CancelSearchV2', search.id);
      if (newGameSearch !== search || search.cancelled) return;
      search.inFlight = false;
      if (String(state?.status).toLowerCase() === 'matched' && state?.gameId) {
        newGameSearch = null;
        renderNewGameSearchState('matched', 'هم‌زمان با لغو، حریف پیدا شد؛ در حال ورود…');
        navigateToGame(state.gameId);
        return;
      }
      search.cancelled = true;
      newGameSearch = null;
      renderNewGameSearchState('idle', 'جست‌وجو لغو شد؛ می‌توانید دوباره شروع کنید.');
    } catch (error) {
      if (newGameSearch !== search || search.cancelled) return;
      search.inFlight = false;
      if (error?.code === 'matchmaking_ack_timeout' && search.recoveryAttempts < 1) {
        search.recoveryAttempts += 1;
        renderNewGameSearchState('reconnecting', 'پاسخ لغو نرسید؛ اتصال تازه و لغو با همان شناسه ادامه می‌یابد.');
        recycleMatchmakingConnection();
        return;
      }
      if (connection.state !== 'Connected') {
        renderNewGameSearchState('reconnecting', 'لغو پس از بازیابی اتصال نهایی می‌شود.');
        return;
      }
      search.cancelled = true;
      newGameSearch = null;
      renderNewGameSearchState('idle', 'جست‌وجو متوقف شد؛ می‌توانید دوباره شروع کنید.');
    }
  }

  function renderNewGameSearchState(state, message) {
    if (!newGameSearchState) return;
    const busy = ['connecting', 'queued', 'reconnecting', 'cancelling', 'matched'].includes(state);
    newGameSearchState.hidden = state === 'idle';
    newGameSearchState.dataset.state = state;
    if (newGameStatus) newGameStatus.textContent = message;
    if (cancelNewGameButton) {
      cancelNewGameButton.hidden = !busy || state === 'matched';
      cancelNewGameButton.disabled = state === 'matched' || state === 'cancelling';
    }
    newGameTimeButtons.forEach(button => { button.disabled = busy; });
    newGameModeButtons.forEach(button => { button.disabled = busy; });
  }

  async function invokeMatchmaking(method, ...args) {
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = window.setTimeout(() => {
        const error = new Error('matchmaking_ack_timeout');
        error.code = 'matchmaking_ack_timeout';
        reject(error);
      }, 10_000);
    });
    try {
      return await Promise.race([connection.invoke(method, ...args), timeout]);
    } finally {
      window.clearTimeout(timer);
    }
  }

  function recycleMatchmakingConnection() {
    if (connection.state === 'Disconnected') {
      void startConnection();
      return;
    }
    void connection.stop().finally(() => {
      window.setTimeout(() => void startConnection(), 300);
    });
  }

  function renderEndOrLiveState(showToast = false) {
    if (!snapshot) return;
    if (snapshot.status === 'Active') {
      showLiveActions();
      return;
    }
    presentEnded(snapshot.result, snapshot.termination, snapshot.status, showToast);
  }

  function presentEnded(result, termination, status = 'Finished', showToast = true, transitionedFromActive = false) {
    if (!snapshot) return;
    const previousStatus = transitionedFromActive ? 'Active' : snapshot.status;
    if (previousStatus === 'Active') mobileResultDismissed = false;
    snapshot.status = status;
    snapshot.result = result;
    snapshot.termination = termination;
    const isAborted = status === 'Aborted';
    cancelQueuedPremove();

    if (previousStatus === 'Active' && !isAborted && snapshot.headToHead) {
      snapshot.headToHead.total += 1;
      if (result === '1-0') snapshot.headToHead.whiteWins += 1;
      else if (result === '0-1') snapshot.headToHead.blackWins += 1;
      else snapshot.headToHead.draws += 1;
      renderHeadToHead(snapshot.headToHead);
    }

    clockBase.finished = true;
    // Realtime presence/rematch/chat updates may still arrive after the game
    // ends. Preserve the user's disposable variation across those updates and
    // initialise it only on the actual transition (or first page load).
    if (previousStatus === 'Active' || !analysisGame) resetAnalysisPosition(reviewPly);
    firstMovePanel.classList.add('hidden');
    disconnectPanel.classList.add('hidden');
    takebackPanel.classList.add('hidden');
    postGamePanel.classList.remove('hidden');
    liveActions.classList.add('hidden');
    mobileGameActions?.classList.add('hidden');
    root.querySelector('[data-online-title]').textContent = isAborted ? 'بازی لغوشده' : 'بازی پایان‌یافته';
    root.querySelector('[data-final-result]').textContent = isAborted ? 'بازی لغو شد' : `نتیجه ${result ?? '*'}`;
    root.querySelector('[data-final-reason]').textContent = isAborted
      ? 'یکی از بازیکنان اولین حرکت خود را در مهلت مقرر انجام نداد؛ امتیازها تغییری نکرد'
      : terminationLabel(termination);
    if (mobileFinalResult) mobileFinalResult.textContent = isAborted ? 'بازی لغو شد' : `نتیجه ${result ?? '*'}`;
    if (mobileFinalReason) mobileFinalReason.textContent = isAborted
      ? 'بدون تغییر امتیاز'
      : terminationLabel(termination);
    revealMobileResultSheet();

    configureTournamentPostGameActions();
    toggleAnalysisButton.classList.toggle('hidden', isAborted);
    renderRematchState();

    const key = `${status}|${result ?? '*'}|${termination ?? ''}`;
    if (showToast && finishedToastKey !== key) {
      finishedToastKey = key;
      toast(isAborted ? 'بازی بدون تغییر امتیاز لغو شد' : `${result ?? '*'} • ${terminationLabel(termination)}`, 'info');
    }
  }

  function showLiveActions() {
    analysisGame = null;
    analysisMoves = [];
    analysisRootPly = 0;
    root.classList.remove('analysis-variation-mode');
    postGamePanel.classList.add('hidden');
    if (mobileResultSheet) mobileResultSheet.hidden = true;
    const hideActions = snapshot?.isSpectator ?? true;
    liveActions.classList.toggle('hidden', hideActions);
    mobileGameActions?.classList.toggle('hidden', hideActions);
    root.querySelector('[data-online-title]').textContent = snapshot?.isSpectator ? 'تماشای زنده' : 'بازی زنده';
    renderActionAvailability();
  }

  function renderActionAvailability() {
    const disabled = !snapshot || snapshot.isSpectator || snapshot.status !== 'Active' || !snapshot.clockStarted;
    drawButtons.forEach(button => { button.disabled = disabled; });
    resignButtons.forEach(button => { button.disabled = disabled; });
    const lastMoveWasWhite = currentPly % 2 === 1;
    const viewerMadeLastMove = snapshot && (lastMoveWasWhite ? snapshot.viewerColor === 'white' : snapshot.viewerColor === 'black');
    const takebackDisabled = disabled || currentPly === 0 || !viewerMadeLastMove
      || snapshot.viewerRequestedTakeback || snapshot.opponentRequestedTakeback;
    takebackButtons.forEach(button => { button.disabled = takebackDisabled; });
  }

  function applyPresence(payload) {
    if (!snapshot || !payload) return;
    setServerTime(payload.serverTimeUtc);

    if (typeof payload.whiteConnected === 'boolean') snapshot.whiteConnected = payload.whiteConnected;
    if (typeof payload.blackConnected === 'boolean') snapshot.blackConnected = payload.blackConnected;

    if (Object.prototype.hasOwnProperty.call(payload, 'firstMoveDeadlineUtc')) {
      snapshot.firstMoveDeadlineUtc = payload.firstMoveDeadlineUtc;
    }

    if (!snapshot.isSpectator) {
      const hasPerColorDisconnectState = Object.prototype.hasOwnProperty.call(payload, 'whiteDisconnectedAtUtc')
        || Object.prototype.hasOwnProperty.call(payload, 'blackDisconnectedAtUtc');

      if (hasPerColorDisconnectState) {
        const opponentIsWhite = snapshot.viewerColor === 'black';
        snapshot.opponentDisconnectedAtUtc = opponentIsWhite
          ? payload.whiteDisconnectedAtUtc
          : payload.blackDisconnectedAtUtc;
        const claimKey = opponentIsWhite ? 'whiteDisconnectClaimAvailableAtUtc' : 'blackDisconnectClaimAvailableAtUtc';
        snapshot.disconnectClaimAvailableAtUtc = Object.prototype.hasOwnProperty.call(payload, claimKey)
          ? payload[claimKey]
          : snapshot.opponentDisconnectedAtUtc
            ? (toMilliseconds(snapshot.opponentDisconnectedAtUtc) ?? serverNow()) + DISCONNECT_GRACE_MILLISECONDS
            : null;
      } else {
        // Initial snapshots already contain the viewer-specific disconnect fields.
        // Keep them intact instead of overwriting them with undefined values.
        if (Object.prototype.hasOwnProperty.call(payload, 'opponentDisconnectedAtUtc')) {
          snapshot.opponentDisconnectedAtUtc = payload.opponentDisconnectedAtUtc;
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'disconnectClaimAvailableAtUtc')) {
          snapshot.disconnectClaimAvailableAtUtc = payload.disconnectClaimAvailableAtUtc;
        }
      }
    }

    renderPresenceDots();
    updateGracePanels();
    // Joining of the second player can make White's first move legal without changing the FEN.
    // Refresh Chessground's movable state immediately instead of waiting for a board change.
    if (ground && reviewPly === currentPly) renderReviewPosition();
  }

  function renderPresenceDots() {
    const whiteDot = root.querySelector('[data-player-presence="white"]');
    const blackDot = root.querySelector('[data-player-presence="black"]');
    setPresenceDot(whiteDot, snapshot?.whiteConnected !== false, 'white');
    setPresenceDot(blackDot, snapshot?.blackConnected !== false, 'black');
  }

  function setPresenceDot(element, connected, color) {
    const isViewerReconnecting = reconnecting && snapshot?.viewerColor === color && !snapshot?.isSpectator;
    element.classList.toggle('offline', !connected && !isViewerReconnecting);
    element.classList.toggle('reconnecting', isViewerReconnecting);
    element.title = isViewerReconnecting ? 'reconnecting…' : connected ? 'آنلاین' : 'آفلاین';
    const label = root.querySelector(`[data-player-network="${color}"]`);
    if (label) label.textContent = isViewerReconnecting ? 'reconnecting…' : connected ? 'آنلاین' : 'آفلاین';
  }

  function updateGracePanels() {
    if (!snapshot) return;
    const now = serverNow();
    const firstDeadline = toMilliseconds(snapshot.firstMoveDeadlineUtc);
    const waitingForBothPlayers = currentPly === 0 && !snapshot.clockStarted && firstDeadline === null;
    const openingMovePending = currentPly <= 1 && (waitingForBothPlayers || firstDeadline !== null);
    const showFirstMove = snapshot.status === 'Active' && openingMovePending;
    firstMovePanel.classList.toggle('hidden', !showFirstMove);
    if (showFirstMove) {
      if (waitingForBothPlayers) {
        firstMoveLabel.textContent = 'در انتظار اتصال هر دو بازیکن';
        firstMoveSeconds.textContent = '…';
        firstMovePanel.classList.remove('urgent');
        firstMovePanel.dataset.openingColor = 'waiting';
      } else {
        const seconds = Math.max(0, Math.ceil((firstDeadline - now) / 1000));
        const openingColor = currentPly === 0 ? 'white' : 'black';
        firstMoveLabel.textContent = openingColor === 'white'
          ? 'مهلت اولین حرکت سفید'
          : 'مهلت اولین حرکت سیاه';
        firstMoveSeconds.textContent = String(seconds);
        firstMovePanel.classList.toggle('urgent', seconds <= 5);
        firstMovePanel.dataset.openingColor = openingColor;
      }
    }

    const claim = currentDisconnectClaim();
    disconnectPanel.classList.toggle('hidden', !claim.visible);
    disconnectActions.classList.toggle('hidden', !claim.visible || !claim.ready);
    root.querySelector('[data-claim-win]').disabled = !claim.visible || !claim.ready;
    root.querySelector('[data-claim-draw]').disabled = !claim.visible || !claim.ready;
    if (!claim.visible) return;
    disconnectCountdown.textContent = String(claim.seconds);
    disconnectMessage.textContent = claim.ready
      ? 'حریف در مهلت مقرر برنگشت؛ نتیجه دلخواه را ثبت کنید.'
      : `${claim.seconds} ثانیه برای بازگشت حریف باقی مانده است.`;
  }

  function currentDisconnectClaim() {
    const clocks = getRawClocks();
    const opponentIsWhite = snapshot?.viewerColor === 'black';
    return getDisconnectClaimState({
      ...snapshot,
      opponentMilliseconds: opponentIsWhite ? clocks.white : clocks.black,
      viewerMilliseconds: opponentIsWhite ? clocks.black : clocks.white
    }, serverNow());
  }

  function updateClocksAndGracePeriods() {
    const clocks = getDisplayedClocks();
    const white = clocks.white;
    const black = clocks.black;
    lastRenderedClocks = { white, black };
    clockHasRendered = true;
    whiteClock.textContent = formatClock(white, true);
    blackClock.textContent = formatClock(black, true);
    whiteClock.classList.toggle('active', !clockBase.finished && clockBase.started && clockBase.whiteTurn);
    blackClock.classList.toggle('active', !clockBase.finished && clockBase.started && !clockBase.whiteTurn);
    whiteClock.classList.toggle('low-time', !clockBase.finished && clockBase.started && white < 10_000);
    blackClock.classList.toggle('low-time', !clockBase.finished && clockBase.started && black < 10_000);
    updateGracePanels();
    renderTakebackCountdown();
  }

  function syncClocks(white, black, whiteTurn, finished, started, serverTimeUtc, smoothDuration = 700) {
    const clientNow = Date.now();
    const previous = clockInitialized && clockHasRendered
      ? { ...lastRenderedClocks }
      : null;
    const authoritativeServerTime = toMilliseconds(serverTimeUtc) ?? serverNow();

    clockBase = {
      white: Math.max(0, Number(white) || 0),
      black: Math.max(0, Number(black) || 0),
      whiteTurn,
      finished,
      started,
      serverAt: authoritativeServerTime
    };
    clockInitialized = true;

    const authoritative = getRawClocks(clientNow);
    if (previous && smoothDuration > 0) {
      const whiteDifference = previous.white - authoritative.white;
      const blackDifference = previous.black - authoritative.black;
      clockCorrection = {
        white: Math.abs(whiteDifference) < 18 ? 0 : whiteDifference,
        black: Math.abs(blackDifference) < 18 ? 0 : blackDifference,
        startedAt: clientNow,
        duration: smoothDuration
      };
    } else {
      clockCorrection = { white: 0, black: 0, startedAt: clientNow, duration: 0 };
    }

    updateClocksAndGracePeriods();
  }

  function getDisplayedClocks(clientNow = Date.now()) {
    const raw = getRawClocks(clientNow);
    if (clockCorrection.duration <= 0) return raw;

    const progress = Math.min(1, Math.max(0, (clientNow - clockCorrection.startedAt) / clockCorrection.duration));
    const remaining = 1 - progress;
    return {
      white: Math.max(0, raw.white + (clockCorrection.white * remaining)),
      black: Math.max(0, raw.black + (clockCorrection.black * remaining))
    };
  }

  function getRawClocks(clientNow = Date.now()) {
    let white = clockBase.white;
    let black = clockBase.black;
    if (!clockBase.finished && clockBase.started) {
      const elapsed = Math.max(0, (clientNow + serverOffsetMilliseconds) - clockBase.serverAt);
      if (clockBase.whiteTurn) white -= elapsed;
      else black -= elapsed;
    }
    return { white: Math.max(0, white), black: Math.max(0, black) };
  }

  function toggleInlineAnalysis() {
    engineVisible = !engineVisible;
    inlineEngine.classList.toggle('hidden', !engineVisible);
    toggleAnalysisButton.textContent = engineVisible ? 'بستن آنالیز' : 'آنالیز بازی';
    if (!engineVisible) {
      if (engine) engine.postMessage('stop');
      engineAcceptingInfo = false;
      root.classList.remove('analysis-variation-mode');
      renderAnalysisContext();
      return;
    }
    root.classList.add('analysis-variation-mode');
    // The board is already in post-game review mode. This button only reveals
    // Stockfish evaluation and must not reset or relocate a local variation.
    if (!analysisGame) resetAnalysisPosition(reviewPly);
    ensureEngine();
    analyzeSelectedPosition();
  }

  function ensureEngine() {
    if (engine) return;
    inlineStatus.textContent = engineRecoveryAttempts > 0
      ? 'در حال بازیابی موتور آنالیز…'
      : 'در حال آماده‌سازی موتور…';
    try {
      const worker = new Worker(stockfishUrl);
      engine = worker;
      engineReady = false;
      engineAcceptingInfo = false;
      worker.addEventListener('message', event => {
        if (engine === worker) handleEngineMessage(String(event.data));
      });
      worker.addEventListener('error', () => recoverEngine(worker));
      worker.addEventListener('messageerror', () => recoverEngine(worker));
      worker.postMessage('uci');
      window.clearTimeout(engineWatchdog);
      engineWatchdog = window.setTimeout(() => recoverEngine(worker), 9_000);
    } catch {
      recoverEngine(null);
    }
  }

  function recoverEngine(failedWorker) {
    if (failedWorker && engine !== failedWorker) return;
    if (engine) {
      try { engine.terminate(); } catch { /* already closed */ }
    }
    engine = null;
    engineReady = false;
    engineAcceptingInfo = false;
    window.clearTimeout(engineWatchdog);
    if (!engineVisible) return;
    if (engineRecoveryAttempts >= 2) {
      inlineStatus.textContent = 'موتور اجرا نشد؛ آنالیز را ببندید و دوباره باز کنید.';
      return;
    }
    engineRecoveryAttempts += 1;
    inlineStatus.textContent = 'ارتباط موتور قطع شد؛ بازیابی خودکار…';
    window.setTimeout(analyzeSelectedPosition, 350 * engineRecoveryAttempts);
  }

  function disposeEngine() {
    window.clearTimeout(engineWatchdog);
    if (engine) {
      try {
        engine.postMessage('stop');
        engine.terminate();
      } catch { /* page is closing */ }
    }
    engine = null;
    engineReady = false;
    engineAcceptingInfo = false;
  }

  function handleEngineMessage(message) {
    if (message === 'uciok') {
      engine.postMessage('setoption name MultiPV value 3');
      engine.postMessage('isready');
      return;
    }
    if (message === 'readyok') {
      engineReady = true;
      engineRecoveryAttempts = 0;
      window.clearTimeout(engineWatchdog);
      if (enginePendingFen) startPendingEngineSearch();
      else inlineStatus.textContent = 'موتور آماده است';
      return;
    }
    if (message.startsWith('bestmove')) {
      engineAcceptingInfo = false;
      if (engineVisible && engineLines.size === 0 && message.includes('(none)')) {
        inlineStatus.textContent = terminalPositionLabel(new Chess(engineFen));
      }
      return;
    }
    if (!engineVisible || !engineAcceptingInfo || !message.startsWith('info ') || !message.includes(' pv ')) return;

    const multipv = Number(matchValue(message, / multipv (\d+)/) ?? 1);
    const depth = Number(matchValue(message, / depth (\d+)/) ?? 0);
    const cp = matchValue(message, / score cp (-?\d+)/);
    const mate = matchValue(message, / score mate (-?\d+)/);
    const pv = matchValue(message, / pv (.+)$/);
    if (!pv) return;
    const score = mate !== null
      ? { type: 'mate', value: Number(mate) }
      : { type: 'cp', value: Number(cp ?? 0) };
    engineLines.set(multipv, { depth, score, pv, fen: engineFen });
    renderEngineLines();
  }

  function analyzeSelectedPosition() {
    if (!engineVisible) return;
    const selectedPosition = isAnalysisMode() ? analysisGame : new Chess(fenAtPly(reviewPly));
    enginePendingFen = selectedPosition.fen();
    enginePendingLabel = analysisMoves.length > 0
      ? `Variation • ${analysisMoves.length.toLocaleString('fa-IR')} نیم‌حرکت`
      : reviewPly === 0
        ? 'آنالیز موقعیت شروع'
        : `آنالیز حرکت ${Math.ceil(reviewPly / 2).toLocaleString('fa-IR')} ${reviewPly % 2 ? 'سفید' : 'سیاه'}`;
    engineLines.clear();
    inlineLines.replaceChildren();
    inlineScore.textContent = '…';
    inlineStatus.textContent = selectedPosition.isGameOver()
      ? terminalPositionLabel(selectedPosition)
      : engineReady ? 'در حال همگام‌سازی موتور…' : 'در حال آماده‌سازی موتور…';
    if (selectedPosition.isGameOver()) {
      inlineScore.textContent = '—';
      engineAcceptingInfo = false;
      enginePendingFen = '';
      if (engine) engine.postMessage('stop');
      return;
    }
    ensureEngine();
    if (!engineReady) return;
    engine.postMessage('stop');
    engineAcceptingInfo = false;
    // Wait for readyok after stop. This prevents late output from the previous
    // search being rendered as evaluation for the newly selected position.
    engine.postMessage('isready');
  }

  function startPendingEngineSearch() {
    if (!engine || !engineReady || !engineVisible || !enginePendingFen) return;
    engineFen = enginePendingFen;
    enginePendingFen = '';
    engineLines.clear();
    inlineLines.replaceChildren();
    inlineStatus.textContent = enginePendingLabel;
    engineAcceptingInfo = true;
    engine.postMessage(`position fen ${engineFen}`);
    engine.postMessage(`go depth ${Number(inlineDepth.value)}`);
  }

  function terminalPositionLabel(position) {
    if (position.isCheckmate()) return 'کیش‌مات؛ این موقعیت ادامهٔ قانونی ندارد.';
    if (position.isStalemate()) return 'پات؛ این موقعیت ادامهٔ قانونی ندارد.';
    return 'بازی در این موقعیت پایان یافته است.';
  }

  function renderEngineLines() {
    inlineLines.replaceChildren();
    const position = new Chess(engineFen);
    for (const [index, line] of [...engineLines.entries()].sort((a, b) => a[0] - b[0])) {
      const item = document.createElement('div');
      item.className = 'inline-engine-line';
      const score = document.createElement('strong');
      score.textContent = formatEvaluation(line.score, position.turn());
      const continuation = document.createElement('span');
      continuation.textContent = pvToSan(line.pv, engineFen);
      item.append(score, continuation);
      inlineLines.append(item);
      if (index === 1) inlineScore.textContent = score.textContent;
    }
    const first = engineLines.get(1);
    if (first) inlineStatus.textContent = `عمق ${first.depth} • ${enginePendingLabel}`;
  }

  function applyRatingUpdate(payload) {
    if (!snapshot || !payload) return;
    const whiteRating = Number(payload.whiteRating);
    const blackRating = Number(payload.blackRating);
    if (!Number.isFinite(whiteRating) || !Number.isFinite(blackRating)) return;
    snapshot.whiteRating = whiteRating;
    snapshot.blackRating = blackRating;
    updateRatingElement('white', whiteRating, Number(payload.whiteRatingDelta) || 0);
    updateRatingElement('black', blackRating, Number(payload.blackRatingDelta) || 0);
  }

  function updateRatingElement(color, rating, delta) {
    const element = root.querySelector(`[data-${color}-rating]`);
    if (!element) return;
    element.textContent = String(rating);
    const container = element.closest('small') ?? element.parentElement;
    container?.querySelector('.rating-live-delta')?.remove();
    if (delta !== 0 && container) {
      const badge = document.createElement('em');
      badge.className = `rating-live-delta ${delta > 0 ? 'positive' : 'negative'}`;
      badge.textContent = `${delta > 0 ? '+' : ''}${delta}`;
      container.append(badge);
      window.setTimeout(() => badge.classList.add('visible'), 20);
    }
    const playerBar = root.querySelector(`[data-online-player="${color}"]`);
    playerBar?.classList.remove('rating-updated');
    requestAnimationFrame(() => playerBar?.classList.add('rating-updated'));
    window.setTimeout(() => playerBar?.classList.remove('rating-updated'), 1400);
  }

  function renderHeadToHead(headToHead) {
    const element = root.querySelector('[data-head-to-head]');
    if (!headToHead || headToHead.total === 0) {
      element.classList.add('hidden');
      return;
    }
    element.classList.remove('hidden');
    root.querySelector('[data-h2h-white]').textContent = headToHead.whiteWins;
    root.querySelector('[data-h2h-draw]').textContent = headToHead.draws;
    root.querySelector('[data-h2h-black]').textContent = headToHead.blackWins;
    root.querySelector('[data-h2h-total]').textContent = headToHead.total;
  }

  function isMyTurn() {
    return snapshot && !snapshot.isSpectator && (game.turn() === 'w'
      ? snapshot.viewerColor === 'white'
      : snapshot.viewerColor === 'black');
  }

  function setServerTime(serverTimeUtc, requestStartedAt = null, responseReceivedAt = Date.now()) {
    const server = toMilliseconds(serverTimeUtc);
    if (server === null) return;

    const hasRoundTripSample = Number.isFinite(requestStartedAt) && Number.isFinite(responseReceivedAt);
    const localReference = hasRoundTripSample
      ? (Number(requestStartedAt) + Number(responseReceivedAt)) / 2
      : Number(responseReceivedAt);
    const sampleOffset = server - localReference;

    if (!serverOffsetInitialized) {
      serverOffsetMilliseconds = sampleOffset;
      serverOffsetInitialized = true;
      return;
    }

    // Push notifications do not provide a round-trip measurement. Reusing them as
    // clock samples would include arbitrary network latency and make timers jump.
    if (!hasRoundTripSample) return;

    const difference = sampleOffset - serverOffsetMilliseconds;
    const adjustment = Math.max(-40, Math.min(40, difference * 0.2));
    serverOffsetMilliseconds += adjustment;
  }

  function serverNow() {
    return Date.now() + (serverOffsetInitialized ? serverOffsetMilliseconds : 0);
  }

  function setConnectionState(connected, text) {
    reconnecting = !connected;
    connectionState.classList.toggle('reconnecting', !connected);
    connectionState.innerHTML = `<span></span> ${text}`;
    renderPresenceDots();
  }

  async function shareCurrentGame() {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'بازی شطرنج آنلاین', text: 'این بازی را زنده تماشا کن:', url });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await copyText(url, 'لینک بازی کپی شد');
  }

  function navigateToGame(newGameId) {
    if (redirecting || !newGameId) return;
    redirecting = true;
    window.location.href = `${gameBaseUrl}${newGameId}`;
  }
}

function normalizeUci(uci) {
  return String(uci ?? '').trim().toLowerCase();
}

function normalizeSearchId(value) {
  return String(value ?? '').trim().toLowerCase();
}

function createSearchId() {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function uciToSquares(uci) {
  if (typeof uci !== 'string' || uci.length < 4) return undefined;
  return [uci.slice(0, 2), uci.slice(2, 4)];
}

function toMilliseconds(value) {
  return parseServerDateTime(value);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function formatTimeControl(initialSeconds, incrementSeconds) {
  const minutes = Number(initialSeconds) / 60;
  return incrementSeconds ? `${minutes} + ${incrementSeconds}` : `${minutes} دقیقه`;
}

function terminationLabel(termination) {
  const labels = {
    Checkmate: 'کیش‌ومات',
    Resignation: 'تسلیم',
    Timeout: 'اتمام زمان',
    DrawAgreement: 'توافق تساوی',
    Stalemate: 'پات',
    InsufficientMaterial: 'کمبود مهره',
    ThreefoldRepetition: 'تکرار سه‌باره',
    FiftyMoveRule: 'قانون پنجاه حرکت',
    Abandoned: 'شروع‌نشدن بازی',
    Disconnection: 'قطع ارتباط حریف'
  };
  return labels[termination] ?? 'بازی پایان یافت';
}

function matchValue(text, expression) {
  return text.match(expression)?.[1] ?? null;
}

function formatEvaluation(score, turn) {
  const value = turn === 'w' ? score.value : -score.value;
  if (score.type === 'mate') return `M${Math.abs(value)}`;
  return `${value >= 0 ? '+' : ''}${(value / 100).toFixed(2)}`;
}

function pvToSan(pv, fen) {
  const replay = new Chess(fen);
  const sans = [];
  for (const uci of pv.split(' ').slice(0, 12)) {
    try {
      const move = replay.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] });
      if (!move) break;
      sans.push(move.san);
    } catch {
      break;
    }
  }
  return sans.join(' ');
}

function toast(title, icon) {
  return Swal.fire({
    toast: true,
    position: 'bottom-start',
    icon,
    title,
    showConfirmButton: false,
    timer: 2400
  });
}

function readableError(error) {
  return String(error?.message ?? error).replace(/^.*HubException:\s*/i, '');
}

function matchmakingError(error) {
  const raw = String(error?.message ?? error).toUpperCase();
  if (raw.includes('MATCHMAKING_AUTH_REQUIRED')) {
    return 'نشست بازی منقضی شده است؛ دوباره وارد حساب کاربری شوید.';
  }
  if (raw.includes('MATCHMAKING_INVALID_TIME_CONTROL')) {
    return 'زمان انتخاب‌شده معتبر نیست؛ یک زمان دیگر انتخاب کنید.';
  }
  return 'ارتباط با سرور دچار مشکل شد. لطفاً دوباره تلاش کنید.';
}

function isPermanentMatchmakingError(error) {
  const raw = String(error?.message ?? error).toUpperCase();
  return raw.includes('MATCHMAKING_INVALID_TIME_CONTROL')
    || raw.includes('MATCHMAKING_INVALID_SEARCH')
    || raw.includes('MATCHMAKING_ACCOUNT_NOT_FOUND')
    || raw.includes('MATCHMAKING_REJECTED');
}
